/**
 * GraphQL queries and hooks — maturityAnalytics pillar.
 * Split from graphql/queries.ts (issue #1409).
 */

import { gql } from '@apollo/client';
import { useQuery, type QueryResult } from '@apollo/client/react';
import type {
  MaturityComparisonSubject,
  MaturityDistributionResult,
  MaturityTrend,
} from '../types/maturityAnalytics';

export const GET_MATURITY_TRENDS = gql`
  query GetMaturityTrends($subjectType: String!, $subjectId: ID!, $frameworkId: ID, $periods: Int) {
    maturityTrends(
      subjectType: $subjectType
      subjectId: $subjectId
      frameworkId: $frameworkId
      periods: $periods
    ) {
      subjectEntityId
      subjectEntityType
      frameworkId
      points {
        assessmentId
        assessmentPeriod
        overallScore
        areaScores {
          areaId
          score
        }
      }
    }
  }
`;

export const GET_MATURITY_COMPARISON = gql`
  query GetMaturityComparison($frameworkId: ID!, $subjectIds: [ID!]!, $assessmentPeriod: String) {
    maturityComparison(
      frameworkId: $frameworkId
      subjectIds: $subjectIds
      assessmentPeriod: $assessmentPeriod
    ) {
      subjectEntityId
      subjectEntityType
      subjectName
      overallScore
      categoryScores {
        categoryId
        score
      }
    }
  }
`;

export const GET_MATURITY_DISTRIBUTION = gql`
  query GetMaturityDistribution($frameworkId: ID!, $assessmentPeriod: String) {
    maturityDistributionByFramework(
      frameworkId: $frameworkId
      assessmentPeriod: $assessmentPeriod
    ) {
      period
      frameworkId
      averageScore
      totalSubjects
      levelCounts {
        level
        count
        percentage
      }
    }
  }
`;

export interface MaturityTrendsVariables {
  subjectType: string;
  subjectId: string;
  frameworkId?: string;
  periods?: number;
}

export function useMaturityTrends(
  variables: MaturityTrendsVariables,
  skip = false
): QueryResult<{ maturityTrends: MaturityTrend | null }> {
  return useQuery(GET_MATURITY_TRENDS, {
    variables,
    skip,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });
}

export interface MaturityComparisonVariables {
  frameworkId: string;
  subjectIds: string[];
  assessmentPeriod?: string;
}

export function useMaturityComparison(
  variables: MaturityComparisonVariables,
  skip = false
): QueryResult<{ maturityComparison: MaturityComparisonSubject[] }> {
  return useQuery(GET_MATURITY_COMPARISON, {
    variables,
    skip,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });
}

export interface MaturityDistributionVariables {
  frameworkId: string;
  assessmentPeriod?: string;
}

export function useMaturityDistribution(
  variables: MaturityDistributionVariables,
  skip = false
): QueryResult<{ maturityDistributionByFramework: MaturityDistributionResult | null }> {
  return useQuery(GET_MATURITY_DISTRIBUTION, {
    variables,
    skip,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });
}

// ─── Tags ─────────────────────────────────────────────────────────────────────
